/**
 * PipelineMonthHeader — month navigation (chevrons + month input), the run's
 * rollup status ("Step N of 6"), the DRY RUN badge, and the Automatic/Manual
 * mode toggle. Before a run starts the toggle edits the draft mode used by
 * Start; on a live pipeline it flips the server-side autoAdvance flag.
 */

import React from 'react';
import { Button, Eyebrow, IconButton, StatusPill, Tabs } from '../../ui';
import { PipelineRun } from '../../../services/adminApi';
import { formatMonthDisplay, overallPill, shiftMonth, PipelinePhase, allStagesComplete } from './pipelineUtils';

type ModeKey = 'auto' | 'manual';

interface PipelineMonthHeaderProps {
  month: string;
  pipeline: PipelineRun | null;
  phase: PipelinePhase;
  actionBusy: string | null;
  draftAuto: boolean;
  onDraftAutoChange: (v: boolean) => void;
  onSelectMonth: (month: string) => void;
  onSetMode: (autoAdvance: boolean) => void;
  onDiscardDryRun: () => void;
}

const PipelineMonthHeader: React.FC<PipelineMonthHeaderProps> = ({
  month,
  pipeline,
  phase,
  actionBusy,
  draftAuto,
  onDraftAutoChange,
  onSelectMonth,
  onSetMode,
  onDiscardDryRun,
}) => {
  const busy = actionBusy !== null;
  const mode: ModeKey = pipeline ? (pipeline.autoAdvance ? 'auto' : 'manual') : draftAuto ? 'auto' : 'manual';
  const rollup = pipeline ? overallPill(pipeline) : null;

  const handleMode = (next: ModeKey) => {
    const auto = next === 'auto';
    if (pipeline && !allStagesComplete(pipeline)) {
      onSetMode(auto);
    } else if (pipeline) {
      // Complete pipeline: flipping mode has no effect on past stages, but keep
      // the server flag in sync so a restart prefills correctly.
      onSetMode(auto);
    } else {
      onDraftAutoChange(auto);
    }
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.left}>
        <Eyebrow>Monthly run</Eyebrow>
        <div style={styles.titleRow}>
          <h2 style={styles.title}>{formatMonthDisplay(month)}</h2>
          <div style={styles.monthNav}>
            <IconButton
              label="Previous month"
              variant="ghost"
              size="sm"
              onClick={() => onSelectMonth(shiftMonth(month, -1))}
            >
              ‹
            </IconButton>
            <input
              type="month"
              aria-label="Select month"
              style={styles.monthInput}
              value={month}
              onChange={(e) => e.target.value && onSelectMonth(e.target.value)}
            />
            <IconButton
              label="Next month"
              variant="ghost"
              size="sm"
              onClick={() => onSelectMonth(shiftMonth(month, 1))}
            >
              ›
            </IconButton>
          </div>
        </div>
        <div style={styles.summaryRow}>
          {rollup && <StatusPill variant={rollup.variant}>{rollup.label}</StatusPill>}
          {pipeline?.dryRun && <StatusPill variant="pending" noDot>dry run</StatusPill>}
          <span style={styles.phaseText}>
            {pipeline ? `Step ${phase.stepIndex} of ${phase.totalSteps} · ${phase.label}` : phase.label}
          </span>
        </div>
      </div>

      <div style={styles.right}>
        <div style={styles.modeWrap}>
          <Tabs<ModeKey>
            value={mode}
            onChange={handleMode}
            size="sm"
            variant="segmented"
            ariaLabel="Pipeline mode"
            options={[
              { value: 'auto', label: 'Automatic', disabled: busy },
              { value: 'manual', label: 'Manual', disabled: busy },
            ]}
          />
          <span style={styles.modeCaption}>
            Automatic runs validation and payout sheets for you; ChannelMeter steps always wait
            for your confirmation.
          </span>
        </div>
        {pipeline?.dryRun && (
          <Button variant="ghost" size="sm" onClick={onDiscardDryRun} disabled={busy}>
            {actionBusy === 'discard' ? 'Discarding…' : 'Discard dry run'}
          </Button>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 24,
    flexWrap: 'wrap',
  },
  left: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minWidth: 0,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flexWrap: 'wrap',
  },
  title: {
    margin: 0,
    fontSize: 26,
    fontWeight: 700,
    letterSpacing: '-0.022em',
    fontFamily: 'var(--font-display)',
    color: 'var(--fg-1)',
  },
  monthNav: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  monthInput: {
    padding: '5px 8px',
    fontSize: 12.5,
    color: 'var(--fg-1)',
    background: 'var(--bg-surface-1)',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-sans)',
    cursor: 'pointer',
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  phaseText: {
    fontSize: 12.5,
    color: 'var(--fg-3)',
  },
  right: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 10,
  },
  modeWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 6,
  },
  modeCaption: {
    fontSize: 11.5,
    color: 'var(--fg-3)',
    maxWidth: 320,
    textAlign: 'right',
    lineHeight: 1.45,
  },
};

export default PipelineMonthHeader;
